import {
  InfoCard,
  InfoCardBody,
  InfoCardHeader,
} from '@/components/ui/InfoCard.tsx';
import MetricList, { MetricListRow } from '@/components/ui/MetricList.tsx';
import { Currency } from '@/features/dashboard/paymentConfig/types/Currency.ts';
import { getCurrencyLabel } from '@/features/dashboard/paymentConfig/types/Currency.utils.ts';
import { MetricDto } from '@/types/stats/MetricDto.ts';
import {
  financeMetricLabel,
  FinanceMetricKey,
} from '@/features/dashboard/tours/types/finance/financeMetrics.utils.ts';
import { findMetric, formatMetricValue } from '@/types/stats/metric.utils.ts';
import { pluralize } from '@/utils/pluralize.ts';

interface BookingValueCardProps {
  /** Płaska lista metryk z całego ekranu; karta wybiera z niej swoją walutę. */
  metrics: MetricDto[];
  currency: Currency;
}

/** Rozpiętość wartości rezerwacji: ile średnio i ile w skrajnych przypadkach. */
const BookingValueCard = ({ metrics, currency }: BookingValueCardProps) => {
  const currencyLabel = getCurrencyLabel(currency);

  const participantCount = findMetric(
    metrics,
    FinanceMetricKey.PARTICIPANT_COUNT,
  );
  const bookingCount = findMetric(metrics, FinanceMetricKey.BOOKING_COUNT);

  const row = (
    key: FinanceMetricKey,
    note: string | undefined,
  ): MetricListRow | null => {
    const metric = findMetric(metrics, key, currency);
    if (!metric) return null;

    return {
      id: key,
      label: financeMetricLabel(key),
      note: note ?? metric.ref?.bookingCode,
      value: formatMetricValue(metric),
    };
  };

  const rows = [
    row(
      FinanceMetricKey.AVG_PER_PARTICIPANT,
      participantCount
        ? pluralize(
            participantCount.value,
            'uczestnik',
            'uczestnicy',
            'uczestników',
          )
        : undefined,
    ),
    row(
      FinanceMetricKey.AVG_PER_BOOKING,
      bookingCount
        ? pluralize(
            bookingCount.value,
            'rezerwacja',
            'rezerwacje',
            'rezerwacji',
          )
        : undefined,
    ),
    row(FinanceMetricKey.HIGHEST, undefined),
    row(FinanceMetricKey.LOWEST, undefined),
  ].filter((entry): entry is MetricListRow => entry !== null);

  return (
    <InfoCard>
      <InfoCardHeader title="Wartość rezerwacji">
        <span className="mt-1 block text-[13px] text-neutral-400">
          w {currencyLabel}
        </span>
      </InfoCardHeader>
      <InfoCardBody className="py-0">
        <MetricList rows={rows} />
      </InfoCardBody>
    </InfoCard>
  );
};

export default BookingValueCard;
